import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import Spinner from "../../components/Shared/Spinner";
import { GetIFRSAttempts } from "../../api/Quiz/GetIFRSAttempts";
import IFRSAttemptsTable from "../../components/Dashboard/IFRSExams/IFRSAttemptsTable";
import { GetIFRSById } from "../../api/Quiz/GetIFRSById";

const IFRSExamStudents = () => {
  const { courseId, exam_id } = useParams<{
    courseId: string;
    exam_id: string;
  }>();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data: IFRSResponse } = useQuery({
    queryFn: () => GetIFRSById(exam_id || ""),
    queryKey: ["ifrs", exam_id],
    enabled: !!exam_id,
    refetchOnWindowFocus: false,
  });

  const { data: AttemptsResponse, isLoading: isAttemptsLoading } = useQuery({
    queryFn: () => GetIFRSAttempts({ page, limit, examId: exam_id || "" }),
    queryKey: ["ifrs-exam-attempts", exam_id, page, limit],
    enabled: !!exam_id,
    staleTime: 0,
    refetchInterval: 0,
    refetchOnWindowFocus: false,
  });

  const attempts = AttemptsResponse?.data?.data || [];
  const meta = AttemptsResponse?.data?.meta;
  const error = AttemptsResponse?.error;
  const examTitle = IFRSResponse?.data?.title || "IFRS Exam";
  const examsPath = `/dashboard/courses/${courseId}/ifrs-exam`;

  if (isAttemptsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="py-7">
      <div className="flex items-center gap-4 mb-7">
        <Link
          to={examsPath}
          className="text-primary hover:text-primary/80 font-semibold"
        >
          ← Back to IFRS Exams
        </Link>
        <h1 className="text-burntBlack font-bold text-[32px]">
          {examTitle} - Attempts
        </h1>
      </div>
      <div className="p-8 rounded-xl bg-white border-black/15 shadow">
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-bold text-2xl">Student Attempts</h1>
          {/* A student may sit the exam repeatedly, so the count is attempts,
              not people. */}
          {!!meta && (
            <span className="text-sm text-gray-500">
              {meta.total} attempt{meta.total === 1 ? "" : "s"}
            </span>
          )}
        </div>

        {error ? (
          <div className="py-8 text-center text-sm text-red-600">
            {error.message || "Failed to load attempts"}
          </div>
        ) : (
          <IFRSAttemptsTable
            data={attempts}
            detailBasePath={`${examsPath}/${exam_id}/students`}
          />
        )}

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={!meta?.hasPreviousPage}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-gray-400"
          >
            Previous
          </button>
          <span>
            Page {meta?.page || page} of {meta?.lastPage || 1}
          </span>
          <button
            onClick={() => setPage((prev) => prev + 1)}
            disabled={!meta?.hasNextPage}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-gray-400"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default IFRSExamStudents;
